import { useCallback, useEffect, useState } from 'react'
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  BackgroundVariant,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { VarGetNode }        from './nodes/rule/VarGetNode'
import { RuleNotNode }       from './nodes/rule/RuleNotNode'
import { RuleCompareNode }   from './nodes/rule/RuleCompareNode'
import { RuleResultNode }    from './nodes/rule/RuleResultNode'
import { RuleAnimTimeNode }  from './nodes/rule/RuleAnimTimeNode'
import { DeleteEdge }        from './nodes/DeleteEdge'
import { EVENTS_ACCENT }     from './nodes/eventsTheme'
import { debugState }        from '../lib/debugState'
import { dropDisplacedEdges } from '../lib/graphConnectPolicy'
import AnchoredOverlay from './AnchoredOverlay'

const RULE_NODE_TYPES = {
  varGet:                VarGetNode,
  ruleNot:               RuleNotNode,
  ruleCompare:           RuleCompareNode,
  ruleResult:            RuleResultNode,
  ruleAnimTimeRemaining: RuleAnimTimeNode,
  ruleAnimTimeRatio:     RuleAnimTimeNode,
}

const RULE_EDGE_TYPES = {
  default: DeleteEdge,
}

const RESULT_NODE = {
  id: 'ruleResult',
  type: 'ruleResult',
  position: { x: 460, y: 160 },
  data: { connected: false },
  deletable: false,
}

let _ruleNodeId = 1
const nextRuleId = () => `rn_${_ruleNodeId++}`

const MENU_ITEMS = [
  { label: '+ Get Variable',        type: 'varGet' },
  { label: '+ NOT',                 type: 'ruleNot' },
  { label: '+ Compare',             type: 'ruleCompare' },
  { label: '+ Anim Time Remaining', type: 'ruleAnimTimeRemaining' },
  { label: '+ Anim Fraction',       type: 'ruleAnimTimeRatio' },
]

// ─── Inner canvas — uses useReactFlow for right-click position ────────────────
function RuleCanvas({ nodes, edges, onNodesChange, onEdgesChange, onConnect, onAdd }) {
  const { screenToFlowPosition } = useReactFlow()
  const [contextMenu, setContextMenu] = useState(null)

  const onPaneContextMenu = useCallback((e) => {
    e.preventDefault()
    const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY })
    setContextMenu({ screenX: e.clientX, screenY: e.clientY, flowPos })
  }, [screenToFlowPosition])

  const closeMenu = useCallback(() => setContextMenu(null), [])

  useEffect(() => {
    if (!contextMenu) return
    const h = () => closeMenu()
    window.addEventListener('mousedown', h)
    return () => window.removeEventListener('mousedown', h)
  }, [contextMenu, closeMenu])

  return (
    <>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onPaneContextMenu={onPaneContextMenu}
        onPaneClick={closeMenu}
        nodeTypes={RULE_NODE_TYPES}
        edgeTypes={RULE_EDGE_TYPES}
        fitView
        fitViewOptions={{ padding: 0.4 }}
        deleteKeyCode="Delete"
        style={{ background: '#10100a' }}
      >
        <Background variant={BackgroundVariant.Dots} color="#222210" gap={20} size={1} />
        <Controls style={{ background: '#1a1a08', border: '1px solid #504010', color: '#c0a030' }} />
      </ReactFlow>

      {contextMenu && (
        <AnchoredOverlay
          x={contextMenu.screenX} y={contextMenu.screenY}
          style={{
            background: '#141408',
            border: '1px solid #404020',
            borderRadius: 6,
            boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
            zIndex: 1000,
            overflow: 'hidden',
            fontFamily: 'var(--m-font)',
            minWidth: 170,
          }}
          onMouseDown={e => e.stopPropagation()}
        >
          <div style={{ padding: '4px 0' }}>
            {MENU_ITEMS.map(item => (
              <div
                key={item.type}
                onClick={() => { onAdd(item.type, contextMenu.flowPos); closeMenu() }}
                style={{
                  padding: '7px 12px',
                  cursor: 'pointer',
                  color: '#c0a030',
                  fontSize: 11,
                  borderLeft: '3px solid #806020',
                  margin: '3px 6px',
                  borderRadius: '0 4px 4px 0',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#1f1f10'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {item.label}
              </div>
            ))}
          </div>
        </AnchoredOverlay>
      )}
    </>
  )
}

// ─── TransitionRuleEditor ─────────────────────────────────────────────────────
export function TransitionRuleEditor({
  smName,
  ruleGraph,
  variables = [],
  activeClipName = null,
  onBack,
  onRuleChange,
  actions = [],
  availableCustomEvents = [],
  onActionsChange,
}) {
  const seedNodes = (ruleGraph?.nodes?.length > 0) ? ruleGraph.nodes : [RESULT_NODE]
  const seedEdges = ruleGraph?.edges ?? []

  const [nodes, setNodes, onNodesChange] = useNodesState(seedNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(seedEdges)

  useEffect(() => {
    onRuleChange?.(nodes, edges)
  }, [nodes, edges]) // eslint-disable-line react-hooks/exhaustive-deps

  // Update result node connected status
  useEffect(() => {
    const connected = edges.some(e => e.target === 'ruleResult')
    setNodes(ns => ns.map(n =>
      n.id === 'ruleResult'
        ? (n.data.connected === connected ? n : { ...n, data: { ...n.data, connected } })
        : n
    ))
  }, [edges]) // eslint-disable-line react-hooks/exhaustive-deps

  // Poll debugState.clipTimings to inject live values into anim time nodes
  useEffect(() => {
    const id = setInterval(() => {
      const timings = activeClipName ? debugState.clipTimings[activeClipName] : null
      setNodes(ns => ns.map(n => {
        if (n.type === 'ruleAnimTimeRemaining') {
          const val = timings?.remaining ?? null
          return n.data._debugValue === val ? n : { ...n, data: { ...n.data, _debugValue: val } }
        }
        if (n.type === 'ruleAnimTimeRatio') {
          const val = timings?.ratio ?? null
          return n.data._debugValue === val ? n : { ...n, data: { ...n.data, _debugValue: val } }
        }
        return n
      }))
    }, 100)
    return () => clearInterval(id)
  }, [activeClipName, setNodes])

  const updateNode = useCallback((nodeId, key, value) => {
    setNodes(ns => ns.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, [key]: value } } : n
    ))
  }, [setNodes])

  const nodesWithHandlers = nodes.map(n => ({
    ...n,
    data: {
      ...n.data,
      variables,
      activeClipName,
      onChange: (key, value) => updateNode(n.id, key, value),
    },
  }))

  const onConnect = useCallback((params) => {
    // Shared Unreal capacity rules (ENG-877) — rule graphs are pure data,
    // so this is the same replace-on-target behavior as before.
    setEdges(es => addEdge({ ...params, type: 'default' }, dropDisplacedEdges(es, params)))
  }, [setEdges])

  const addNode = useCallback((type, position) => {
    const id = nextRuleId()
    const pos = position ?? { x: 150 + Math.random() * 100, y: 100 + Math.random() * 100 }
    if (type === 'varGet') {
      setNodes(ns => [...ns, { id, type, position: pos, data: { varName: variables[0]?.name ?? '' } }])
    } else if (type === 'ruleNot') {
      setNodes(ns => [...ns, { id, type, position: pos, data: {} }])
    } else if (type === 'ruleCompare') {
      setNodes(ns => [...ns, { id, type, position: pos, data: { operator: '>', threshold: 0 } }])
    } else if (type === 'ruleAnimTimeRemaining') {
      setNodes(ns => [...ns, { id, type, position: pos, data: { subtype: 'remaining' } }])
    } else if (type === 'ruleAnimTimeRatio') {
      setNodes(ns => [...ns, { id, type, position: pos, data: { subtype: 'ratio' } }])
    }
  }, [setNodes, variables])

  return (
    <div style={{ width: '100%', height: '100%', background: '#10100a', position: 'relative' }}>

      {/* Breadcrumb */}
      <div style={{
        position: 'absolute', top: 10, left: 10, zIndex: 10,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <button
          onClick={onBack}
          style={{
            background: '#1a1a0a', border: '1px solid #604020', color: '#c08040',
            borderRadius: 4, padding: '5px 12px', fontSize: 11,
            fontFamily: 'var(--m-font)', cursor: 'pointer',
          }}
        >
          ← Back
        </button>
        <span style={{ color: '#404030', fontSize: 11, fontFamily: 'var(--m-font)' }}>
          Blueprint / {smName} /
        </span>
        <span style={{ color: '#c0a030', fontSize: 11, fontFamily: 'var(--m-font)', fontWeight: 'bold' }}>
          Transition Rule
        </span>
      </div>

      {/* Toolbar */}
      <div style={{
        position: 'absolute', top: 10, left: 240, zIndex: 10,
        display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap',
      }}>
        {MENU_ITEMS.map(item => (
          <button key={item.type} onClick={() => addNode(item.type)} style={ruleBtn('#1e1e08', '#806020')}>
            {item.label}
          </button>
        ))}
        <span style={{ color: '#302818', fontSize: 10, fontFamily: 'var(--m-font)' }}>
          right-click or use buttons · connect to &quot;Can Enter Transition&quot; to activate
        </span>
      </div>

      {/* Actions panel (ENG-1062) — custom events this transition fires once,
          when it is taken. Alongside the condition (the rule graph): condition
          decides WHETHER to switch; actions are what it DOES on switching. */}
      <TransitionActionsPanel
        actions={actions}
        available={availableCustomEvents}
        onChange={onActionsChange}
      />

      <ReactFlowProvider>
        <RuleCanvas
          nodes={nodesWithHandlers}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onAdd={addNode}
        />
      </ReactFlowProvider>
    </div>
  )
}

// ─── Actions panel ────────────────────────────────────────────────────────────
function TransitionActionsPanel({ actions = [], available = [], onChange }) {
  const remove = (name) => onChange?.(actions.filter(a => a !== name))
  const add = (name) => { if (name && !actions.includes(name)) onChange?.([...actions, name]) }
  // Choosable = declared custom events not already added to this transition.
  const choosable = available.filter(n => !actions.includes(n))

  // Themed via --m-* tokens; the Events-family identity color is the functional
  // EVENTS_ACCENT constant (imported, not a hex literal) — see paletteEnforcement.
  return (
    <div
      onMouseDown={e => e.stopPropagation()}
      style={{
        position: 'absolute', top: 10, right: 10, zIndex: 10,
        width: 220, background: 'var(--m-panel)', border: '1px solid var(--m-line)',
        borderRadius: 'var(--m-radius)', boxShadow: 'var(--m-shadow-card)',
        fontFamily: 'var(--m-font)', overflow: 'hidden',
      }}
    >
      <div style={{
        background: 'var(--m-panel-hi)', padding: '6px 10px',
        color: EVENTS_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <span style={{ fontSize: 12 }}>⚡</span> Actions
      </div>

      <div style={{ padding: '8px 10px' }}>
        <div style={{ color: 'var(--m-fg-3)', fontSize: 9, marginBottom: 6 }}>
          Custom events fired once when this transition is taken.
        </div>

        {actions.length === 0 && (
          <div style={{ color: 'var(--m-fg-3)', fontSize: 10, marginBottom: 6 }}>No actions.</div>
        )}

        {actions.map(name => (
          <div key={name} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 6, background: 'var(--m-node-bg)', border: '1px solid var(--m-line)',
            borderRadius: 'var(--m-radius-sm)', padding: '4px 7px', marginBottom: 4,
          }}>
            <span style={{ color: EVENTS_ACCENT, fontSize: 10, fontFamily: 'var(--m-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {name}
            </span>
            <button
              onClick={() => remove(name)}
              title="Remove action"
              style={{
                background: 'transparent', border: 'none', color: 'var(--m-fg-2)',
                cursor: 'pointer', fontSize: 12, lineHeight: 1, padding: 0,
              }}
            >✕</button>
          </div>
        ))}

        {choosable.length > 0 ? (
          <select
            value=""
            onChange={e => { add(e.target.value); e.target.value = '' }}
            style={{
              width: '100%', boxSizing: 'border-box', marginTop: 2,
              background: 'var(--m-bg-graph)', border: '1px solid var(--m-line)',
              color: 'var(--m-accent)', padding: '4px 6px', borderRadius: 'var(--m-radius-sm)',
              fontSize: 10, fontFamily: 'var(--m-font)', outline: 'none', cursor: 'pointer',
            }}
          >
            <option value="" disabled>+ Add action…</option>
            {choosable.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        ) : (
          <div style={{ color: 'var(--m-fg-3)', fontSize: 9, marginTop: 4, lineHeight: 1.4 }}>
            {available.length === 0
              ? 'No custom events yet. Add a Custom Event node in the Event graph and name it.'
              : 'All custom events already added.'}
          </div>
        )}
      </div>
    </div>
  )
}

const ruleBtn = (bg, border) => ({
  background: bg,
  border: `1px solid ${border}`,
  color: '#eee',
  borderRadius: 4,
  padding: '5px 10px',
  fontSize: 11,
  fontFamily: 'var(--m-font)',
  cursor: 'pointer',
})
